// Repeat visits: the report's answers are kept on the device, so opening a report seen
// before shows the remembered numbers straight away while the same requests run again
// and replace them a few seconds later. Only successful answers are kept; a failed or
// unavailable store simply means no early copy (the page then loads as it always did).
const PREFIX = "kit-answer:";
const MAX_ENTRIES = 30;
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
const MAX_BYTES = 1500 * 1024;

type Entry = { t: number; v: any };

const keys = (): string[] => {
  const out: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) out.push(k);
    }
  } catch (e) {}
  return out;
};

const ageOf = (k: string): number => {
  try {
    return (JSON.parse(localStorage.getItem(k) || "{}") as Entry).t || 0;
  } catch (e) {
    return 0;
  }
};

// oldest first
const byAge = () =>
  keys()
    .map((k) => ({ k, t: ageOf(k) }))
    .sort((a, b) => a.t - b.t)
    .map((e) => e.k);

const read = (key: string): any => {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return undefined;
    const entry = JSON.parse(raw) as Entry;
    if (!entry || Date.now() - entry.t > MAX_AGE_MS) return undefined;
    return entry.v;
  } catch (e) {
    return undefined;
  }
};

const write = (key: string, value: any) => {
  let raw: string;
  try {
    raw = JSON.stringify({ t: Date.now(), v: value });
  } catch (e) {
    return;
  }
  if (raw.length > MAX_BYTES) return;
  const full = PREFIX + key;
  const old = byAge().filter((k) => k !== full);
  // keep the newest MAX_ENTRIES (this one included)
  while (old.length >= MAX_ENTRIES) {
    try {
      localStorage.removeItem(old.shift() as string);
    } catch (e) {}
  }
  // storage full: drop the oldest until it fits, or give up
  for (;;) {
    try {
      localStorage.setItem(full, raw);
      return;
    } catch (e) {
      const k = old.shift();
      if (!k) return;
      try {
        localStorage.removeItem(k);
      } catch (e2) {
        return;
      }
    }
  }
};

export const answerKey = (endpoint: string, body: any) =>
  `${endpoint}|${JSON.stringify(body)}`;

// Hands any remembered answer to `early` (synchronously, before the request goes out),
// then fetches; a successful answer is remembered and returned. `ok` decides whether an
// answer is worth keeping (default: anything that did not throw).
export const remembered = async <T>(
  key: string,
  fetcher: () => Promise<T>,
  early?: (value: T) => void,
  ok: (value: T) => boolean = () => true
): Promise<T> => {
  if (early) {
    const cached = read(key);
    if (cached !== undefined) {
      try {
        early(cached);
      } catch (e) {}
    }
  }
  const value = await fetcher();
  if (ok(value)) write(key, value);
  return value;
};

export const forgetAnswers = () => {
  keys().forEach((k) => {
    try {
      localStorage.removeItem(k);
    } catch (e) {}
  });
};
